import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1, Volume2, VolumeX, ListMusic, Heart, Radio, X, AudioLines } from "lucide-react";
import { EqualizerDialog } from "@/components/EqualizerDialog";
import { Thumbs } from "@/lib/ratings";
import { Marquee } from "@/components/Marquee";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { QueuePeek } from "@/components/QueuePeek";
import { AudiobookControls } from "@/components/AudiobookControls";
import { Visualizer } from "@/components/Visualizer";
import { NowPlaying } from "@/components/NowPlaying";
import { rgb, rgba, useArtTint } from "@/lib/artColor";
import { useLedStyle, useVizMode } from "@/lib/vizMode";
import { StationLogo, LiveDot } from "@/components/radio/StationLogo";
import { CaptureButton } from "@/components/radio/CaptureButton";
import { Slider } from "@/components/ui/slider";
import { Cover } from "@/components/Cover";
import { usePlayer } from "@/context/PlayerContext";
import { api, fmtTime } from "@/lib/api";

const IconBtn = ({ onClick, active, children, testid, title, disabled }) => (
  <button onClick={onClick} data-testid={testid} title={title} disabled={disabled}
    className={`rounded-full p-2 transition-colors duration-150 disabled:opacity-30 ${active ? "text-amber-600" : "text-zinc-500 hover:text-zinc-900"}`}>
    {children}
  </button>
);

export const PlayerBar = ({ onQueue }) => {
  const p = usePlayer();
  const [eqOpen, setEqOpen] = useState(false);
  const [npOpen, setNpOpen] = useState(false);
  const t = p.current;
  const tint = useArtTint(t && !t.live ? t.album_id : null);
  const [viz] = useVizMode();
  const [ledStyle] = useLedStyle();
  const star = async () => {
    if (t.live) {
      const { data } = await api.patch(`/radio/stations/${t.station.id}`, { favorite: !t.station.favorite });
      p.updateTrack(t.id, { station: { ...t.station, favorite: data.favorite } });
      return;
    }
    const { data } = await api.post(`/tracks/${t.id}/star`);
    p.updateTrack(t.id, { starred: data.starred });
  };
  const liked = t?.live ? t.station?.favorite : t?.starred;
  const upNext = p.nextTrack && p.nextTrack.id !== t?.id ? p.nextTrack : null;
  const hint = (label, tr, keys) => (
    <span className="flex items-center gap-2.5" data-testid={`${label.toLowerCase()}-hint`}>
      {tr && <Cover albumId={tr.album_id} className="h-9 w-9 shrink-0 shadow-md" rounded="rounded" iconSize={14} />}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label} · {keys}</span>
        {tr ? <span className="max-w-[260px] truncate">{tr.artist} — {tr.title}</span> : <span className="text-zinc-400">{label === "Previous" ? "Restart this song" : "End of queue"}</span>}
      </span>
    </span>
  );
  return (
    <footer className="fixed bottom-0 left-0 right-0 z-50 flex h-20 items-center gap-4 border-t border-zinc-200/80 bg-white/85 px-4 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] backdrop-blur-xl transition-[box-shadow] duration-700 sm:h-24 sm:px-8" style={{ "--tint": rgb(tint), boxShadow: `0 -8px 30px rgba(0,0,0,0.04), 0 -1px 0 ${rgba(tint, 0.35)}` }} data-tint={rgb(tint)} data-testid="player-bar">
      <div className="flex w-1/3 min-w-0 items-center gap-3">
        {t?.live ? (
          <>
            <button onClick={() => setNpOpen(true)} className="shrink-0 transition-transform hover:scale-105" data-testid="player-open-now-playing" title="Now playing"><StationLogo src={t.favicon} className="h-12 w-12 shadow-sm sm:h-14 sm:w-14" /></button>
            <div className="min-w-0 flex-1">
              <Marquee className="text-sm font-medium" testid="player-track-title">{t.title}</Marquee>
              <Marquee className="text-xs text-zinc-500" testid="player-now-playing">{p.nowPlaying || "Live radio"}</Marquee>
            </div>
            {p.nowPlaying && <CaptureButton nowPlaying={p.nowPlaying} station={t.title} />}
            {t.station?.id && (
              <IconBtn onClick={star} active={liked} testid="player-star-button" title="Favourite station">
                <Heart size={16} fill={liked ? "currentColor" : "none"} />
              </IconBtn>
            )}
          </>
        ) : t ? (
          <>
            <button onClick={() => setNpOpen(true)} className="shrink-0 transition-transform hover:scale-105" data-testid="player-open-now-playing" title="Now playing"><Cover albumId={t.album_id} className="h-12 w-12 shadow-sm sm:h-14 sm:w-14" iconSize={18} /></button>
            <div className="min-w-0 flex-1">
              <Marquee className="text-sm font-medium" testid="player-track-title">{t.title}</Marquee>
              <Marquee className="text-xs text-zinc-500">
                {t.shared_pid || t.lib ? <span data-testid="player-track-artist">{t.artist}</span> :
                  <Link to={`/artist?name=${encodeURIComponent(t.artist)}`} className="hover:underline" data-testid="player-track-artist">{t.artist}</Link>}
              </Marquee>
            </div>
            {!t.shared_pid && !t.lib && (
              <IconBtn onClick={star} active={t.starred} testid="player-star-button" title="Favorite">
                <Heart size={16} fill={t.starred ? "currentColor" : "none"} />
              </IconBtn>
            )}
            {!t.shared_pid && <Thumbs trackId={t.id} size={15} testid="player-rate" className="hidden sm:flex" />}
          </>
        ) : (
          <p className="font-mono text-xs text-zinc-400" data-testid="player-empty">Nothing playing</p>
        )}
      </div>

      <div className="flex flex-1 flex-col items-center gap-1">
        <TooltipProvider delayDuration={150}>
        <div className="flex items-center gap-1 sm:gap-2">
          {t?.is_audiobook ? <AudiobookControls compact /> : <IconBtn onClick={() => p.setShuffle(!p.shuffle)} active={p.shuffle} testid="player-shuffle-button" title="Shuffle (S)"><Shuffle size={16} /></IconBtn>}
          <Tooltip><TooltipTrigger asChild><span><IconBtn onClick={p.prev} testid="player-skip-prev-button" disabled={!t || t.live} title=""><SkipBack size={18} fill="currentColor" /></IconBtn></span></TooltipTrigger>
            <TooltipContent side="top" className="rounded-lg bg-zinc-900 px-3 py-2 text-xs text-white" data-testid="player-prev-tooltip">{hint("Previous", p.prevTrack, "Shift+←")}</TooltipContent></Tooltip>
          <button onClick={p.toggle} disabled={!t} data-testid="player-play-pause-button" title={p.playing ? "Pause (Space)" : "Play (Space)"}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900 text-white transition-transform duration-150 hover:scale-105 active:scale-95 disabled:opacity-40">
            {p.playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
          </button>
          <Tooltip><TooltipTrigger asChild><span><IconBtn onClick={p.next} testid="player-skip-next-button" disabled={!t || t.live} title=""><SkipForward size={18} fill="currentColor" /></IconBtn></span></TooltipTrigger>
            <TooltipContent side="top" className="rounded-lg bg-zinc-900 px-3 py-2 text-xs text-white" data-testid="player-next-tooltip">{hint("Next", upNext, "Shift+→")}</TooltipContent></Tooltip>
          <IconBtn onClick={p.cycleRepeat} active={p.repeat !== "off"} testid="player-repeat-button" title={`Repeat: ${p.repeat} (R)`}>
            {p.repeat === "one" ? <Repeat1 size={16} /> : <Repeat size={16} />}
          </IconBtn>
        </div>
        </TooltipProvider>
        {t?.live ? (
          <div className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-red-600 sm:flex" data-testid="player-live-badge">
            <LiveDot />Live
          </div>
        ) : (
        <div className="hidden w-full max-w-xl items-center gap-3 sm:flex">
          <span className="w-10 text-right font-mono text-[11px] text-zinc-500" data-testid="player-elapsed">{fmtTime(p.time)}</span>
          <Slider data-testid="player-progress-bar" value={[Math.min(p.time, p.duration || 0)]} max={p.duration || 1} step={1}
            onValueChange={([v]) => p.seek(v)} disabled={!t} className="flex-1 [&>span:first-child>span]:bg-[var(--tint)] [&>span:first-child>span]:transition-colors [&>span:first-child>span]:duration-700 [&_[role=slider]]:border-[var(--tint)]" />
          <span className="w-10 font-mono text-[11px] text-zinc-500" data-testid="player-duration">{fmtTime(p.duration)}</span>
          {p.crossfading && <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] text-rose-700" data-testid="player-crossfading">crossfade</span>}
        </div>
        )}
      </div>

      <div className="flex w-1/3 min-w-0 items-center justify-end gap-2">
        {upNext && (
          <HoverCard openDelay={250} closeDelay={150}>
            <HoverCardTrigger asChild>
          <button onClick={p.next} title={`Up next: ${upNext.title} — ${upNext.artist}`} data-testid="player-up-next"
            className="hidden min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 py-1 text-left transition-colors hover:bg-zinc-100 lg:flex lg:max-w-[260px] xl:max-w-[340px] 2xl:max-w-[420px]">
            <Cover albumId={upNext.album_id} className="h-9 w-9 shrink-0 shadow-sm" rounded="rounded" iconSize={14} />
            <span className="flex min-w-0 flex-1 flex-col items-start">
              <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Up next</span>
              <Marquee className="w-full text-xs text-zinc-700" testid="player-up-next-text">{upNext.title}<span className="text-zinc-400"> · {upNext.artist}</span></Marquee>
            </span>
          </button>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="end" sideOffset={10} className="w-80 rounded-xl border-zinc-200/80 bg-white/95 p-2 shadow-xl backdrop-blur-xl">
              <QueuePeek onOpenQueue={onQueue} />
            </HoverCardContent>
          </HoverCard>
        )}
        {p.radio && (
          <span className="hidden items-center gap-1.5 rounded-full bg-amber-50 py-1 pl-3 pr-1 text-xs text-amber-800 xl:flex" data-testid="player-radio-chip">
            <Radio size={12} /><span className="max-w-[160px] truncate">{p.radio.label}</span>
            <button onClick={p.stopRadio} title="Stop endless radio" data-testid="player-radio-stop" className="rounded-full p-0.5 hover:bg-amber-100"><X size={12} /></button>
          </span>
        )}
        <IconBtn onClick={() => p.setMuted(!p.muted)} testid="player-mute-button" title="Mute (M)">
          {p.muted || p.volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </IconBtn>
        <Slider data-testid="player-volume-slider" value={[p.muted ? 0 : p.volume * 100]} max={100} step={1}
          onValueChange={([v]) => p.setVolume(v / 100)} className="hidden w-24 lg:flex" />
        <button onClick={() => setNpOpen(true)} className="hidden h-8 w-16 items-end md:flex" data-testid="player-mini-visualizer" title="Now playing"><Visualizer bars={14} playing={p.playing} mode={viz} ledStyle={ledStyle} color={rgb(tint)} dim={rgba(tint, 0.15)} className="h-7 w-full" testid="player-visualizer" /></button>
        <IconBtn onClick={() => setEqOpen(true)} testid="player-eq-toggle" title="Equalizer">
          <AudioLines size={17} className={p.eq.enabled && p.eq.preset !== "Flat" ? "text-amber-600" : ""} />
        </IconBtn>
        <IconBtn onClick={onQueue} testid="player-queue-toggle" title="Queue">
          <ListMusic size={18} />
        </IconBtn>
      </div>
      <EqualizerDialog open={eqOpen} onOpenChange={setEqOpen} />
      <NowPlaying open={npOpen} onClose={() => setNpOpen(false)} onStar={star} onEq={() => setEqOpen(true)} />
    </footer>
  );
};
